import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import SearchProgressLogs from './common/SearchProgressLogs';
import { generateInstagramPostUrl } from '../utils/postUrlHelper';

interface InstagramCreatorsSearchModalProps {
  showInstagramModal: boolean;
  setShowInstagramModal: (show: boolean) => void;
  instagramSearchResult: any;
  setInstagramSearchResult: (result: any) => void;
  searchLogs: string[];
  clearSearchLogs: () => void;
  showSearchLogs: boolean;
  setShowSearchLogs: (show: boolean) => void;
  instagramSearchKeyword: string;
  setInstagramSearchKeyword: (keyword: string) => void;
  instagramMinViews: number;
  setInstagramMinViews: (views: number) => void;
  instagramMaxItems: number;
  setInstagramMaxItems: (items: number) => void;
  instagramSearching: boolean;
  searchInstagramCreators: () => void;
  instagramAddingCreators: Set<string>;
  instagramAddedCreators: Set<string>;
  instagramExistingCreators: Set<string>;
  addInstagramCreator: (post: any) => void;
  instagramEmailStatus: Record<string, any>;
  fetchInstagramEmail: (username: string) => void;
}

const InstagramCreatorsSearchModal: React.FC<InstagramCreatorsSearchModalProps> = ({
  showInstagramModal,
  setShowInstagramModal,
  instagramSearchResult,
  setInstagramSearchResult,
  searchLogs,
  clearSearchLogs,
  showSearchLogs,
  setShowSearchLogs,
  instagramSearchKeyword,
  setInstagramSearchKeyword,
  instagramMinViews,
  setInstagramMinViews,
  instagramMaxItems,
  setInstagramMaxItems,
  instagramSearching,
  searchInstagramCreators,
  instagramAddingCreators,
  instagramAddedCreators,
  instagramExistingCreators,
  addInstagramCreator,
  instagramEmailStatus,
  fetchInstagramEmail
}) => {
  // Local state for email filter
  const [instagramEmailFilter, setInstagramEmailFilter] = useState(false);
  
  // State for post selection
  const [selectedPosts, setSelectedPosts] = useState<Set<string>>(new Set());
  const [bulkAddingPosts, setBulkAddingPosts] = useState(false);
  const [bulkAddProgress, setBulkAddProgress] = useState({ current: 0, total: 0 });
  
  // State for automatic email fetching
  const [emailFetchProgress, setEmailFetchProgress] = useState({ current: 0, total: 0 });
  const [isAutoFetchingEmails, setIsAutoFetchingEmails] = useState(false);
  const [currentlyFetchingUsernames, setCurrentlyFetchingUsernames] = useState<Set<string>>(new Set());
  const abortControllerRef = useRef<AbortController | null>(null);

  const closeModal = () => {
    // Abort any ongoing email fetching
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsAutoFetchingEmails(false);
    setCurrentlyFetchingUsernames(new Set());
    setEmailFetchProgress({ current: 0, total: 0 });
    setSelectedPosts(new Set());
    setBulkAddingPosts(false);
    setShowInstagramModal(false);
    setInstagramSearchResult(null);
    clearSearchLogs();
  };

  // Helper function to format Instagram views
  const formatInstagramViews = (views: number): string => {
    if (!views) return '0';
    if (views >= 1000000) return `${(views / 1000000).toFixed(1)}M`;
    if (views >= 1000) return `${(views / 1000).toFixed(1)}K`;
    return views.toString();
  };

  // Helper functions for selection
  const togglePostSelection = (postUrl: string) => {
    setSelectedPosts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(postUrl)) {
        newSet.delete(postUrl);
      } else {
        newSet.add(postUrl);
      }
      return newSet;
    });
  };

  const toggleSelectAllPosts = () => {
    if (!instagramSearchResult?.data) return;
    
    const filteredPosts = instagramSearchResult.data.filter((post: any) => {
      if (!instagramEmailFilter) return true;
      const emailStatus = instagramEmailStatus[post.ownerUsername];
      return emailStatus?.email || (emailStatus?.emails && emailStatus.emails.length > 0);
    });
    
    const availablePosts = filteredPosts.filter((post: any) => {
      const creatorId = post.ownerUsername;
      const isAdding = instagramAddingCreators.has(creatorId);
      const isAdded = instagramAddedCreators.has(creatorId);
      const isExisting = instagramExistingCreators.has(post.ownerUsername?.toLowerCase());
      return !isAdding && !isAdded && !isExisting;
    });

    const availablePostUrls = availablePosts.map((post: any) => 
      post.url || generateInstagramPostUrl(post.shortCode)
    );
    
    const allSelected = availablePostUrls.length > 0 && availablePostUrls.every((url: string) => selectedPosts.has(url));
    
    if (allSelected) {
      // Deselect all
      setSelectedPosts(prev => {
        const newSet = new Set(prev);
        availablePostUrls.forEach((url: string) => newSet.delete(url));
        return newSet;
      });
    } else {
      // Select all available
      setSelectedPosts(prev => {
        const newSet = new Set(prev);
        availablePostUrls.forEach((url: string) => newSet.add(url));
        return newSet;
      });
    }
  };

  const addSelectedPosts = async () => {
    if (selectedPosts.size === 0) return;
    
    setBulkAddingPosts(true);
    const postsToAdd = instagramSearchResult.data.filter((post: any) => {
      const postUrl = post.url || generateInstagramPostUrl(post.shortCode);
      return selectedPosts.has(postUrl);
    });
    
    setBulkAddProgress({ current: 0, total: postsToAdd.length });
    
    for (let i = 0; i < postsToAdd.length; i++) {
      const post = postsToAdd[i];
      setBulkAddProgress({ current: i + 1, total: postsToAdd.length });
      
      try {
        await addInstagramCreator(post);
        // Small delay between adds to avoid overwhelming the server
        if (i < postsToAdd.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      } catch (error) {
        console.error(`Error adding creator ${post.ownerUsername}:`, error);
      }
    }
    
    setSelectedPosts(new Set());
    setBulkAddingPosts(false);
    setBulkAddProgress({ current: 0, total: 0 });
  };

  // Automatic email fetching when search results are loaded
  useEffect(() => {
    if (instagramSearchResult?.data && instagramSearchResult.data.length > 0 && !instagramSearching) {
      fetchAllEmails();
    }
    
    return () => {
      // Cleanup: abort any ongoing requests when component unmounts or result changes
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [instagramSearchResult, instagramSearching]);

  // Function to fetch emails for all creators with parallel batching
  const fetchAllEmails = async () => {
    // Filter posts that need email fetching
    const postsNeedingEmail = instagramSearchResult.data.filter((post: any) => {
      const emailStatus = instagramEmailStatus[post.ownerUsername];
      // Skip if already has email or already checked
      return !emailStatus || (!emailStatus.email && !emailStatus.checked && emailStatus.status !== 'found' && emailStatus.status !== 'not-found');
    });

    if (postsNeedingEmail.length === 0) return;

    // Create new abort controller for this batch
    abortControllerRef.current = new AbortController();
    
    setIsAutoFetchingEmails(true);
    setEmailFetchProgress({ current: 0, total: postsNeedingEmail.length });

    const BATCH_SIZE = 4; // Process 4 requests in parallel (Apify rate limit consideration)
    let processedCount = 0;

    try {
      // Process posts in batches
      for (let i = 0; i < postsNeedingEmail.length; i += BATCH_SIZE) {
        // Check if aborted
        if (abortControllerRef.current.signal.aborted) {
          break;
        }

        // Get current batch
        const batch = postsNeedingEmail.slice(i, i + BATCH_SIZE);
        
        // Set currently processing usernames for the batch
        const batchUsernames = batch.map((post: any) => post.ownerUsername);
        
        // Process batch in parallel
        const batchPromises = batch.map(async (post: any) => {
          try {
            // Add username to currently processing set
            setCurrentlyFetchingUsernames(prev => new Set([...Array.from(prev), post.ownerUsername]));
            await fetchInstagramEmail(post.ownerUsername);
            // Remove username from currently processing set
            setCurrentlyFetchingUsernames(prev => {
              const newSet = new Set(prev);
              newSet.delete(post.ownerUsername);
              return newSet;
            });
          } catch (error) {
            console.error(`Error fetching email for ${post.ownerUsername}:`, error);
            // Remove username from currently processing set on error
            setCurrentlyFetchingUsernames(prev => {
              const newSet = new Set(prev);
              newSet.delete(post.ownerUsername);
              return newSet;
            });
          }
        });

        // Wait for all requests in the batch to complete
        await Promise.all(batchPromises);
        
        // Update progress
        processedCount += batch.length;
        setEmailFetchProgress({ current: processedCount, total: postsNeedingEmail.length });
        
        // Add delay between batches to avoid rate limiting (200ms)
        if (i + BATCH_SIZE < postsNeedingEmail.length) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }
    } catch (error) {
      console.error('Error during automatic email fetching:', error);
    } finally {
      setIsAutoFetchingEmails(false);
      setCurrentlyFetchingUsernames(new Set());
      abortControllerRef.current = null;
      
      // Automatically enable email filter when email checking is complete
      setInstagramEmailFilter(true);
    }
  };

  if (!showInstagramModal) return null;

  return (
    <>
      {/* Instagram Search Modal */}
      {createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: 'var(--space-4)'
          }}
          onClick={closeModal}
        >
          <div 
            className="bg-primary rounded-xl shadow-xl"
            style={{
              maxWidth: '90%',
              width: '900px',
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: 'var(--space-8)', borderBottom: '1px solid var(--border-color)' }}>
              {!instagramSearchResult ? (
                /* 검색 전 - 검색 UI 표시 */
                <>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h2 className="text-2xl font-bold text-primary">
                        Find Instagram Creators
                      </h2>
                      <div className="mt-3">
                        <label className="block text-sm font-medium mb-1">Search Term</label>
                        <input
                          type="text"
                          value={instagramSearchKeyword}
                          onChange={(e) => setInstagramSearchKeyword(e.target.value)}
                          className="input w-full"
                          placeholder="Enter keyword"
                          disabled={instagramSearching}
                        />
                      </div>
                    </div>
                    <button 
                      className="btn btn-secondary"
                      onClick={closeModal}
                    >
                      ✕
                    </button>
                  </div>

                  {/* Parameter Selection */}
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Minimum Views</label>
                      <input
                        type="number"
                        value={instagramMinViews || ''}
                        onChange={(e) => setInstagramMinViews(e.target.value ? parseInt(e.target.value) : 0)}
                        className="input w-full"
                        placeholder="Enter minimum views"
                        disabled={instagramSearching}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Max Results</label>
                      <input
                        type="number"
                        value={instagramMaxItems}
                        onChange={(e) => setInstagramMaxItems(e.target.value ? Math.min(parseInt(e.target.value), 1000) : 30)}
                        className="input w-full"
                        placeholder="Max results (default: 30)"
                        min="1"
                        max="1000"
                        disabled={instagramSearching}
                      />
                    </div>
                  </div>

                  <button
                    className="btn btn-primary w-full mt-4"
                    style={{ 
                      background: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)'
                    }}
                    onClick={() => searchInstagramCreators()}
                    disabled={instagramSearching || !instagramSearchKeyword.trim()}
                  >
                    {instagramSearching ? 'Searching...' : 'Search Instagram Keyword'}
                  </button>

                  {/* Search Logs Section */}
                  <SearchProgressLogs
                    logs={searchLogs}
                    isExpanded={showSearchLogs}
                    onToggle={() => setShowSearchLogs(!showSearchLogs)}
                    maxHeight="200px"
                  />
                </>
              ) : (
                /* 검색 후 - 뒤로가기 버튼과 결과 정보만 표시 */
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <button 
                      className="btn btn-secondary"
                      onClick={() => {
                        setInstagramSearchResult(null);
                        clearSearchLogs();
                      }}
                      style={{ padding: '8px 20px' }}
                    >
                      ← Back
                    </button>
                    <div className="flex-1">
                      <h2 className="text-xl font-bold text-primary">
                        Search Results: {instagramSearchKeyword}
                      </h2>
                      {!instagramSearching && instagramSearchResult && !instagramSearchResult.error && (
                        <>
                          <div className="flex items-center gap-4 mt-2 flex-wrap">
                            <p className="text-sm text-secondary">
                              Found {instagramSearchResult.count || (Array.isArray(instagramSearchResult.data) ? instagramSearchResult.data.length : 0)} posts
                            </p>
                          </div>
                          
                          {/* Download Raw Data Button */}
                          {instagramSearchResult && (
                            <div className="mt-2">
                              <button 
                                onClick={() => {
                                  const dataStr = JSON.stringify(instagramSearchResult, null, 2);
                                  const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
                                  const exportFileDefaultName = `instagram-search-${instagramSearchKeyword}-${new Date().toISOString()}.json`;
                                  
                                  const linkElement = document.createElement('a');
                                  linkElement.setAttribute('href', dataUri);
                                  linkElement.setAttribute('download', exportFileDefaultName);
                                  linkElement.click();
                                }}
                                className="text-xs text-gray-600 hover:text-gray-800 underline"
                              >
                                📥 Download Raw Data
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <button 
                    className="btn btn-secondary"
                    onClick={closeModal}
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>

            {/* Results area */}
            {instagramSearchResult && !instagramSearchResult.error && (
              <div style={{ 
                flex: 1, 
                overflow: 'auto',
                padding: 'var(--space-6)'
              }}>
                {/* Search Logs at the top of results - Same as TikTokCreatorsSearchModal */}
                {searchLogs.length > 0 && (
                  <SearchProgressLogs
                    logs={searchLogs}
                    isExpanded={showSearchLogs}
                    onToggle={() => setShowSearchLogs(!showSearchLogs)}
                    maxHeight="150px"
                  />
                )}
                
                {instagramSearching ? (
                  <div className="text-center py-8">
                    <div className="loading" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                      <div style={{
                        width: '24px',
                        height: '24px',
                        border: '3px solid #333',
                        borderTop: '3px solid #e1306c',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      <span>Searching Instagram creators...</span>
                    </div>
                  </div>
                ) : (
                  <div>
                    {/* Bulk Adding Progress Bar */}
                    {bulkAddingPosts && (
                      <div style={{
                        marginBottom: '16px',
                        padding: '12px',
                        backgroundColor: '#2a2a2a',
                        borderRadius: '8px',
                        border: '1px solid #444'
                      }}>
                        <div style={{ 
                          fontSize: '12px', 
                          color: '#fff', 
                          marginBottom: '8px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <span>➕ Adding creators: {bulkAddProgress.current} / {bulkAddProgress.total}</span>
                        </div>
                        <div style={{ 
                          width: '100%', 
                          height: '4px', 
                          backgroundColor: '#444',
                          borderRadius: '2px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${bulkAddProgress.total > 0 ? (bulkAddProgress.current / bulkAddProgress.total) * 100 : 0}%`,
                            height: '100%',
                            backgroundColor: '#10b981',
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                      </div>
                    )}
                    
                    {/* Email Fetching Progress Bar */}
                    {isAutoFetchingEmails && (
                      <div style={{
                        marginBottom: '16px',
                        padding: '12px',
                        backgroundColor: '#2a2a2a',
                        borderRadius: '8px',
                        border: '1px solid #444'
                      }}>
                        <div style={{ 
                          fontSize: '12px', 
                          color: '#fff', 
                          marginBottom: '8px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <span>📧 Checking emails: {emailFetchProgress.current} / {emailFetchProgress.total}</span>
                          <button
                            onClick={() => {
                              if (abortControllerRef.current) {
                                abortControllerRef.current.abort();
                              }
                            }}
                            style={{
                              padding: '4px 8px',
                              fontSize: '10px',
                              backgroundColor: '#ff4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            Stop
                          </button>
                        </div>
                        <div style={{ 
                          width: '100%', 
                          height: '4px', 
                          backgroundColor: '#444',
                          borderRadius: '2px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${emailFetchProgress.total > 0 ? (emailFetchProgress.current / emailFetchProgress.total) * 100 : 0}%`,
                            height: '100%',
                            backgroundColor: '#667eea',
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                      </div>
                    )}
                    
                    {/* Email Filter Toggle */}
                    {instagramSearchResult.data && instagramSearchResult.data.length > 0 && (
                      <div style={{
                        marginBottom: '16px',
                        padding: '12px',
                        backgroundColor: 'var(--bg-secondary)',
                        borderRadius: 'var(--radius-lg)',
                        border: '1px solid var(--border-color)'
                      }}>
                        <label style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          cursor: 'pointer',
                          color: 'var(--text-primary)',
                          fontSize: '14px'
                        }}>
                          <input
                            type="checkbox"
                            checked={instagramEmailFilter}
                            onChange={(e) => setInstagramEmailFilter(e.target.checked)}
                            style={{
                              width: '16px',
                              height: '16px',
                              cursor: 'pointer'
                            }}
                          />
                          <span>Only show creators with emails</span>
                        </label>
                      </div>
                    )}

                    {/* Select All Checkbox */}
                    {instagramSearchResult.data && instagramSearchResult.data.length > 0 && (() => {
                      const filteredPosts = instagramSearchResult.data.filter((post: any) => {
                        if (!instagramEmailFilter) return true;
                        const emailStatus = instagramEmailStatus[post.ownerUsername];
                        return emailStatus?.email || (emailStatus?.emails && emailStatus.emails.length > 0);
                      });
                      
                      const availablePosts = filteredPosts.filter((post: any) => {
                        const creatorId = post.ownerUsername;
                        const isAdding = instagramAddingCreators.has(creatorId);
                        const isAdded = instagramAddedCreators.has(creatorId);
                        const isExisting = instagramExistingCreators.has(post.ownerUsername?.toLowerCase());
                        return !isAdding && !isAdded && !isExisting;
                      });

                      const availablePostUrls = availablePosts.map((post: any) => 
      post.url || generateInstagramPostUrl(post.shortCode)
    );
                      const selectedCount = availablePostUrls.filter((url: string) => selectedPosts.has(url)).length;

                      return availablePosts.length > 0 ? (
                        <div style={{
                          marginBottom: '16px',
                          padding: '12px',
                          backgroundColor: 'var(--bg-secondary)',
                          borderRadius: 'var(--radius-lg)',
                          border: '1px solid var(--border-color)',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: 'pointer',
                            color: 'var(--text-primary)',
                            fontSize: '14px'
                          }}>
                            <input
                              type="checkbox"
                              checked={availablePostUrls.length > 0 && availablePostUrls.every((url: string) => selectedPosts.has(url))}
                              onChange={toggleSelectAllPosts}
                              style={{
                                width: '16px',
                                height: '16px',
                                cursor: 'pointer'
                              }}
                            />
                            <span>Select All New ({availablePosts.length} available)</span>
                          </label>
                          
                          {selectedPosts.size > 0 && (
                            <button
                              onClick={addSelectedPosts}
                              disabled={bulkAddingPosts}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: bulkAddingPosts ? '#666' : '#667eea',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                cursor: bulkAddingPosts ? 'not-allowed' : 'pointer'
                              }}
                            >
                              {bulkAddingPosts ? `Adding ${selectedCount}...` : `Add Selected (${selectedCount})`}
                            </button>
                          )}
                        </div>
                      ) : null;
                    })()}
                    
                    {instagramSearchResult.data && instagramSearchResult.data.length > 0 ? (
                      <div style={{ 
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                        gap: '16px',
                        padding: '16px 0'
                      }}>
                        {instagramSearchResult.data
                          .filter((post: any) => {
                            if (!instagramEmailFilter) return true;
                            const emailStatus = instagramEmailStatus[post.ownerUsername];
                            return emailStatus?.email || (emailStatus?.emails && emailStatus.emails.length > 0);
                          })
                          .map((post: any, index: number) => {
                            const creatorId = post.ownerUsername || post.ownerId;
                            const isAdding = instagramAddingCreators.has(creatorId);
                            const isAdded = instagramAddedCreators.has(creatorId);
                            const isExisting = instagramExistingCreators.has(post.ownerUsername?.toLowerCase());
                            const emailStatus = instagramEmailStatus[post.ownerUsername] || {};
                            const hasEmail = emailStatus.email || (emailStatus.emails && emailStatus.emails.length > 0);
                            
                            const isSelectable = !isAdding && !isAdded && !isExisting;
                            const postUrl = post.url || generateInstagramPostUrl(post.shortCode);
                            const isSelected = selectedPosts.has(postUrl);
                            
                            return (
                              <div 
                                key={index} 
                                style={{ 
                                  backgroundColor: isExisting ? '#1a2a1a' : '#2a2a2a',
                                  borderRadius: '12px',
                                  overflow: 'hidden',
                                  transition: 'transform 0.2s, box-shadow 0.2s',
                                  border: currentlyFetchingUsernames.has(post.ownerUsername) 
                                    ? '2px solid #2563eb' 
                                    : isSelected
                                      ? '2px solid #667eea'
                                      : isExisting 
                                        ? '2px solid #51cf66' 
                                        : '1px solid #333',
                                  position: 'relative',
                                  boxShadow: currentlyFetchingUsernames.has(post.ownerUsername) 
                                    ? '0 0 15px rgba(37, 99, 235, 0.5)' 
                                    : isSelected 
                                      ? '0 0 10px rgba(102, 126, 234, 0.3)'
                                      : 'none'
                                }}
                                onMouseEnter={(e) => {
                                  if (!currentlyFetchingUsernames.has(post.ownerUsername)) {
                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.4)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!currentlyFetchingUsernames.has(post.ownerUsername)) {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                  } else {
                                    // Keep the blue glow for currently processing card
                                    e.currentTarget.style.boxShadow = '0 0 15px rgba(37, 99, 235, 0.5)';
                                  }
                                }}
                              >
                                <a 
                                  href={post.url || `https://www.instagram.com/p/${post.shortCode}/`} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  style={{ textDecoration: 'none', color: 'inherit' }}
                                >
                                  {/* Thumbnail */}
                                  {post.displayUrl || post.thumbnail ? (
                                    <div style={{
                                      width: '100%',
                                      paddingBottom: '100%',
                                      position: 'relative',
                                      backgroundColor: '#000'
                                    }}>
                                      <img 
                                        src={post.displayUrl || post.thumbnail}
                                        alt="Instagram Post" 
                                        style={{ 
                                          position: 'absolute',
                                          top: 0,
                                          left: 0,
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover'
                                        }}
                                        onError={(e) => {
                                          e.currentTarget.style.display = 'none';
                                          e.currentTarget.parentElement!.style.backgroundColor = '#333';
                                        }}
                                      />
                                      {/* Stats Overlay */}
                                      <div style={{
                                        position: 'absolute',
                                        bottom: '8px',
                                        left: '8px',
                                        right: '8px',
                                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                        color: '#fff',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        fontSize: '10px',
                                        display: 'flex',
                                        justifyContent: 'space-around',
                                        alignItems: 'center',
                                        gap: '4px'
                                      }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                          ❤️ {formatInstagramViews(post.likesCount || 0)}
                                        </span>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                          💬 {formatInstagramViews(post.commentsCount || 0)}
                                        </span>
                                        {post.videoPlayCount && (
                                          <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                            👁️ {formatInstagramViews(post.videoPlayCount)}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <div style={{
                                      width: '100%',
                                      paddingBottom: '100%',
                                      backgroundColor: '#333',
                                      position: 'relative'
                                    }}>
                                      <span style={{ 
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        color: '#666'
                                      }}>No Image</span>
                                    </div>
                                  )}
                                </a>
                                
                                {/* Selection Checkbox */}
                                {isSelectable && (
                                  <div style={{
                                    position: 'absolute',
                                    top: '8px',
                                    left: '8px',
                                    zIndex: 10,
                                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                    borderRadius: '4px',
                                    padding: '4px'
                                  }}>
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        const postUrl = post.url || generateInstagramPostUrl(post.shortCode);
                                        togglePostSelection(postUrl);
                                      }}
                                      style={{
                                        width: '16px',
                                        height: '16px',
                                        cursor: 'pointer'
                                      }}
                                    />
                                  </div>
                                )}
                                
                                {/* Creator Info */}
                                <div style={{ padding: '12px' }}>
                                  <a 
                                    href={`https://www.instagram.com/${post.ownerUsername}/`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ textDecoration: 'none', color: 'inherit' }}
                                  >
                                    <div style={{ 
                                      fontSize: '13px',
                                      fontWeight: 'bold',
                                      color: '#fff',
                                      marginBottom: '4px',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      @{post.ownerUsername}
                                    </div>
                                  </a>
                                  
                                  {/* Email Status and Fetch Button */}
                                  <div style={{ marginBottom: '8px' }}>
                                    {currentlyFetchingUsernames.has(post.ownerUsername) ? (
                                      <div style={{ 
                                        fontSize: '11px', 
                                        color: '#2563eb', 
                                        fontWeight: 'bold',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                      }}>
                                        <span style={{ animation: 'pulse 2s infinite' }}>⚡</span>
                                        이메일 확인 중...
                                      </div>
                                    ) : emailStatus.loading ? (
                                      <div style={{ fontSize: '11px', color: '#888' }}>
                                        ⏳ Fetching email...
                                      </div>
                                    ) : emailStatus.email ? (
                                      <div style={{
                                        backgroundColor: '#00ff00',
                                        color: '#000',
                                        borderRadius: '4px',
                                        padding: '4px 6px',
                                        fontSize: '10px',
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        marginTop: '4px'
                                      }}>
                                        📧 {emailStatus.email}
                                      </div>
                                    ) : emailStatus.emails && emailStatus.emails.length > 0 ? (
                                      <div style={{
                                        backgroundColor: '#ffd700',
                                        color: '#000',
                                        borderRadius: '4px',
                                        padding: '4px 6px',
                                        fontSize: '10px',
                                        textAlign: 'center',
                                        marginTop: '4px'
                                      }}>
                                        📧 {emailStatus.emails.length} possible emails
                                      </div>
                                    ) : emailStatus.checked || emailStatus.status === 'not-found' ? (
                                      <div style={{ fontSize: '11px', color: '#868e96' }}>
                                        ✗ No email found
                                      </div>
                                    ) : isAutoFetchingEmails ? (
                                      <div style={{ fontSize: '11px', color: '#888' }}>
                                        ⏳ Waiting... ({emailFetchProgress.current}/{emailFetchProgress.total})
                                      </div>
                                    ) : (
                                      <button
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          fetchInstagramEmail(post.ownerUsername);
                                        }}
                                        style={{
                                          width: '100%',
                                          padding: '4px 8px',
                                          backgroundColor: '#667eea',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '4px',
                                          fontSize: '10px',
                                          fontWeight: 'bold',
                                          cursor: 'pointer',
                                          transition: 'background-color 0.2s'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = '#764ba2';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = '#667eea';
                                        }}
                                      >
                                        🔍 Find Email
                                      </button>
                                    )}
                                  </div>
                                  
                                  {/* Caption */}
                                  {post.caption && (
                                    <div style={{ 
                                      fontSize: '11px',
                                      color: '#888',
                                      marginBottom: '8px',
                                      overflow: 'hidden',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      lineHeight: '1.4'
                                    }}>
                                      {post.caption}
                                    </div>
                                  )}
                                  
                                  {/* Follower Count */}
                                  {post.ownerFollowerCount && (
                                    <div style={{
                                      fontSize: '11px',
                                      color: '#aaa',
                                      marginBottom: '8px'
                                    }}>
                                      👥 {formatInstagramViews(post.ownerFollowerCount)} followers
                                    </div>
                                  )}
                                  
                                  {/* Action Button */}
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (!bulkAddingPosts && !isAdding && !isAdded && !isExisting) {
                                        addInstagramCreator(post);
                                      }
                                    }}
                                    disabled={bulkAddingPosts || isAdding || isAdded || isExisting}
                                    style={{
                                      width: '100%',
                                      padding: '6px 12px',
                                      backgroundColor: isExisting ? '#51cf66' : 
                                                      isAdded ? '#868e96' : 
                                                      isAdding ? '#666' : 
                                                      bulkAddingPosts ? '#444' : 
                                                      selectedPosts.has(postUrl) ? '#667eea' : 
                                                      '#ff7b00',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                                      fontSize: '11px',
                                      fontWeight: 'bold',
                                      cursor: (bulkAddingPosts || isAdding || isAdded || isExisting) ? 'not-allowed' : 'pointer',
                                      transition: 'background-color 0.2s',
                                      opacity: isExisting ? 0.8 : 
                                              isAdded ? 0.6 : 
                                              (isAdding || bulkAddingPosts) ? 0.7 : 1
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!bulkAddingPosts && !isAdding && !isAdded && !isExisting) {
                                        e.currentTarget.style.backgroundColor = selectedPosts.has(postUrl) ? '#764ba2' : '#ff9500';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!bulkAddingPosts && !isAdding && !isAdded && !isExisting) {
                                        e.currentTarget.style.backgroundColor = selectedPosts.has(postUrl) ? '#667eea' : '#ff7b00';
                                      }
                                    }}
                                  >
                                    {isExisting ? '✓ Already in Database' : 
                                     isAdded ? '✓ Already Added' : 
                                     isAdding ? '⏳ Adding...' : 
                                     bulkAddingPosts && selectedPosts.has(postUrl) ? '⏳ In Queue...' :
                                     selectedPosts.has(postUrl) ? '✓ Selected' :
                                     '➕ Add Creator'}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                        <div style={{ fontSize: '2rem', marginBottom: '20px' }}>📷</div>
                        <p>No Instagram posts found for this search.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Error display */}
            {instagramSearchResult && instagramSearchResult.error && (
              <div style={{ padding: 'var(--space-6)' }}>
                <div className="text-center py-8">
                  <p className="text-red-500">❌ {instagramSearchResult.error}</p>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default InstagramCreatorsSearchModal;